/**
 * DeptDesigPage.js — Departments & Designations
 *
 * Validations added:
 * 1. Name: required, min 2 chars, max 100 chars, letters/spaces/hyphens only
 * 2. window.confirm → ConfirmModal for deletes
 * 3. errors state + attempted flag → isInvalid on name field
 * 4. Real-time error clear as user types
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Table, Badge,
    Modal, Form, Spinner,
} from 'react-bootstrap';
import { Plus, Edit2, Trash2, Layers, Award } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

// ── Shared Confirm Modal ──────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm"/> : 'Delete'}
            </Button>
        </Modal.Footer>
    </Modal>
);

// ── Validation ────────────────────────────────────────────────────────────
const NAME_REGEX = /^[a-zA-Z0-9\s\-&'.\/()]+$/;

const validateName = (name, label) => {
    const n = name?.trim();
    if (!n)              return `${label} name is required`;
    if (n.length < 2)    return `${label} name must be at least 2 characters`;
    if (n.length > 100)  return `${label} name cannot exceed 100 characters`;
    if (!NAME_REGEX.test(n)) return `${label} name contains invalid characters`;
    return '';
};

// ════════════════════════════════════════════════════════════════════════════
const DeptDesigPage = () => {
    const [departments,  setDepartments]  = useState([]);
    const [designations, setDesignations] = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [modal,        setModal]        = useState({ show: false, type: 'dept', editing: null });
    const [form,         setForm]         = useState({ name: '' });
    const [nameError,    setNameError]    = useState('');
    const [attempted,    setAttempted]    = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [confirm,      setConfirm]      = useState({ show: false, type: '', id: null, name: '', loading: false });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [deptRes, desigRes] = await Promise.all([
                axiosInstance.get('/hr/departments'),
                axiosInstance.get('/hr/designations'),
            ]);
            setDepartments(deptRes.data.data  || []);
            setDesignations(desigRes.data.data || []);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchAll(); }, []);

    const openCreate = (type) => {
        setModal({ show: true, type, editing: null });
        setForm({ name: '' });
        setNameError('');
        setAttempted(false);
    };

    const openEdit = (type, item) => {
        setModal({ show: true, type, editing: item });
        setForm({ name: item.name });
        setNameError('');
        setAttempted(false);
    };

    const closeModal = () => {
        setModal(m => ({ ...m, show: false }));
        setNameError('');
        setAttempted(false);
    };

    const typeLabel = modal.type === 'dept' ? 'Department' : 'Designation';

    const handleNameChange = (val) => {
        setForm({ name: val });
        if (attempted) setNameError(validateName(val, typeLabel));
    };

    const handleSave = async () => {
        setAttempted(true);
        const err = validateName(form.name, typeLabel);
        if (err) { setNameError(err); return; }
        const endpoint = modal.type === 'dept' ? '/hr/departments' : '/hr/designations';
        setSaving(true);
        try {
            const payload = { name: form.name.trim() };
            if (modal.editing) {
                await axiosInstance.put(`${endpoint}/${modal.editing.id}`, payload);
                toast.success(`${typeLabel} updated`);
            } else {
                await axiosInstance.post(endpoint, payload);
                toast.success(`${typeLabel} created`);
            }
            closeModal();
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    const handleDeleteConfirm = async () => {
        setConfirm(p => ({ ...p, loading: true }));
        const endpoint = confirm.type === 'dept' ? '/hr/departments' : '/hr/designations';
        try {
            await axiosInstance.delete(`${endpoint}/${confirm.id}`);
            toast.success('Deleted');
            fetchAll();
        } catch { toast.error('Delete failed — item may be in use'); }
        finally { setConfirm({ show: false, type: '', id: null, name: '', loading: false }); }
    };

    const ItemTable = ({ items, type }) => {
        const lbl = type === 'dept' ? 'Department' : 'Designation';
        return (
            <Table hover responsive className="mb-0 small align-middle">
                <thead className="bg-light">
                    <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                        <th className="ps-4">{lbl}</th>
                        <th>Employees</th>
                        <th className="text-end pe-4">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length > 0 ? items.map(item => (
                        <tr key={item.id}>
                            <td className="ps-4 fw-medium">{item.name}</td>
                            <td><Badge bg="light" text="dark">{item.employee_count || 0}</Badge></td>
                            <td className="text-end pe-4">
                                <Button variant="link" size="sm" className="text-muted p-1"
                                    onClick={() => openEdit(type, item)}>
                                    <Edit2 size={15}/>
                                </Button>
                                <Button variant="link" size="sm" className="text-danger p-1"
                                    onClick={() => setConfirm({
                                        show: true, type, id: item.id, name: item.name, loading: false,
                                    })}>
                                    <Trash2 size={15}/>
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={3} className="text-center py-4 text-muted">
                                No {type === 'dept' ? 'departments' : 'designations'} yet.
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        );
    };

    return (
        <div className="p-2 p-md-4">
            <div className="mb-4">
                <h4 className="fw-bold mb-1">Departments &amp; Designations</h4>
                <p className="text-muted small">Manage your organisation structure</p>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : (
                <Row className="g-4">
                    <Col xs={12} lg={6}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0">
                                    <Layers size={18} className="me-2 text-primary"/>
                                    Departments ({departments.length})
                                </h6>
                                <Button variant="primary" size="sm"
                                    className="d-flex align-items-center gap-1"
                                    onClick={() => openCreate('dept')}>
                                    <Plus size={16}/>Add
                                </Button>
                            </Card.Header>
                            <ItemTable items={departments} type="dept"/>
                        </Card>
                    </Col>
                    <Col xs={12} lg={6}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0">
                                    <Award size={18} className="me-2 text-primary"/>
                                    Designations ({designations.length})
                                </h6>
                                <Button variant="primary" size="sm"
                                    className="d-flex align-items-center gap-1"
                                    onClick={() => openCreate('desig')}>
                                    <Plus size={16}/>Add
                                </Button>
                            </Card.Header>
                            <ItemTable items={designations} type="desig"/>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Add / Edit Modal */}
            <Modal show={modal.show} onHide={closeModal} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">
                        {modal.editing ? 'Edit' : 'Add'} {typeLabel}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Label className="small fw-bold">{typeLabel} Name *</Form.Label>
                    <Form.Control
                        placeholder={modal.type === 'dept'
                            ? 'e.g. Engineering'
                            : 'e.g. Senior Software Engineer'}
                        value={form.name}
                        maxLength={100}
                        isInvalid={attempted && !!nameError}
                        onChange={e => handleNameChange(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                        autoFocus/>
                    <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
                    <Form.Text className="text-muted">
                        Letters, numbers, spaces, hyphens and &amp; allowed (max 100 chars)
                    </Form.Text>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={closeModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : modal.editing ? 'Save Changes' : 'Create'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirm Modal */}
            <ConfirmModal
                show={confirm.show}
                title={`Delete ${confirm.type === 'dept' ? 'Department' : 'Designation'}`}
                message={`Delete "${confirm.name}"? Employees assigned to it will be unlinked.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirm({ show: false, type: '', id: null, name: '', loading: false })}
                loading={confirm.loading}/>
        </div>
    );
};

export default DeptDesigPage;
